import React from 'react';
import { OGMeta, getOGMeta } from '../../../api/og_meta';
import { Card, Icon } from 'semantic-ui-react';

interface Props {
    url: string;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp', 'webp'];

const fetchOGMetaClientSide = async (url: string): Promise<OGMeta> => {
    const res = await fetch(url).catch(() => null);
    if (!res || !res.ok || res.status === 202) {
        throw new Error('Failed to fetch');
    }
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const get = (prop: string) =>
        doc.querySelector(`meta[property='${prop}']`)?.getAttribute('content') ?? '';
    return {
        image: get('og:image'),
        title: get('og:title'),
        description: get('og:description'),
        site_name: get('og:site_name'),
    };
};

const PreviewImage = ({ url }: Props) => {
    const [meta, setMeta] = React.useState<OGMeta | undefined>(undefined);

    React.useEffect(() => {
        if (!url) {
            return;
        }

        if (IMAGE_EXTENSIONS.some((ext) => url.toLowerCase().endsWith(`.${ext}`))) {
            setMeta({ title: url.split('/').pop() || '', description: '', image: url, site_name: '' });
            return;
        }

        fetchOGMetaClientSide(url)
            .catch(() => getOGMeta(url).then((res) => res.data))  // fallback to server
            .then((data) => setMeta(data))
            .catch(() => setMeta({}));
    }, [url]);

    if (!meta) {
        return <span><Icon name='spinner' loading /></span>;
    }
    if (Object.keys(meta).length === 0) {
        return <span><Icon name='stop circle' color='red' /></span>;
    }
    return (
        <Card
            image={meta.image}
            header={meta.title}
            meta={meta.site_name}
            description={meta.description}
            className='sql-result-preview-image'
        />
    );
};

export default PreviewImage;